import { useState, useEffect, useRef } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import '../../order/order-list.scss';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../../components/table/data-table';
import {
  Order,
  OrderPaymentMethod,
  OrderReturnStatus,
  OrderSearchRequest,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import {
  AddressCell,
  AssignedUnitCell,
  CustomerNameCell,
  DateTimeCell,
  FormatAmountCell,
  OrderNumberCell,
  LocationCell,
  OrderFeesCell,
  OrderFeesTotalCell,
  OrderStatusCell,
  PaymentMethodCell,
  ProductFeesCell,
  ProductFeesTotalCell,
  RefundReasonsCell,
  RentalEndCell,
  RentalStartCell,
  TotalAmountCell,
  TotalRefundAmountCell,
  TotalTaxAmountCell,
  TotalUpchargeAmountCell,
  UpchargeReasonsCell,
} from './../../order/order-table-cells';
import { Location } from '../../../clients/quipli-service/location/location';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { SearchDropdownOption } from '../../../components/dropdown/search-dropdown';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SelectForm } from '../../../components/select-form/select-form';
import { DateTime, Interval } from 'luxon';
import FileSaver from 'file-saver';
import { OrdersTotals, sumByLineItemType } from '../../order/order-utils';
import { QuipliLink } from '../../../components/link/quipli-link';
import './transaction-report.scss';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { User } from '../../../clients/quipli-service/user/user';
import { TabNavRequest } from '../../../components/tab-nav-v2/tab-nav-request';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { displayMoneyFormat } from '../../../util/util';
import { UserSearch } from '../../../components/user-search/user-search';
import { Badge } from '../../../components/badge/badge';
import { uniqBy } from 'lodash';
import { formatShippingType, ShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

export const TransactionsReport = () => {
  const {
    searchOrders,
    listLocations,
    exportTransactionsReport,
    getOrdersResume,
    getMultipleConfigurations,
    getUserById,
  } = useAPI();

  const searchRef = useRef<any>(null);

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true),
    [tableData, setTableData] = useState<DataTableRow[]>([]),
    [tablePagination, setTablePagination] = useState<PaginationData>(),
    [locations, setLocations] = useState<Location[]>([]),
    [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>('unitNumber'),
    [timezone, setTimezone] = useState<string>(),
    [isResumeLoading, setIsResumeLoading] = useState<boolean>(false),
    [orderTotals, setOrderTotals] = useState<OrdersTotals | null>(null);

  const [requestedAfter, setRequestedAfter] = useState<DateTime | undefined>();
  const [requestedBefore, setRequestedBefore] = useState<DateTime | undefined>();
  const [rentalStartStartDate, setRentalStartStartDate] = useState<DateTime | undefined>();
  const [rentalStartEndDate, setRentalStartEndDate] = useState<DateTime | undefined>();
  const [rentalEndStartDate, setRentalEndStartDate] = useState<DateTime | undefined>();
  const [rentalEndEndDate, setRentalEndEndDate] = useState<DateTime | undefined>();
  const [startDate, setStartDate] = useState<DateTime | undefined>();
  const [endDate, setEndDate] = useState<DateTime | undefined>();
  const [selectedUser, setSelectedUser] = useState<User>();
  const [selectedOrderType, setSelectedOrderType] = useState<OrderType>();
  const [selectedLocation, setSelectedLocation] = useState<number>();
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<ShippingType>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<OrderPaymentMethod>();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const {
    request,
    setRequest,
    numOfParams: activeFilters,
  } = useRequestQueryParams<OrderSearchRequest & { tab?: string }>(
    {
      tab: params.has('tab') ? params.get('tab')! : 'All',
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      from: params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined,
      to: params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined,
      startDateFrom: params.has('startDateFrom') ? DateTime.fromISO(params.get('startDateFrom')!) : undefined,
      startDateTo: params.has('startDateTo') ? DateTime.fromISO(params.get('startDateTo')!) : undefined,
      endDateFrom: params.has('endDateFrom') ? DateTime.fromISO(params.get('endDateFrom')!) : undefined,
      endDateTo: params.has('endDateTo') ? DateTime.fromISO(params.get('endDateTo')!) : undefined,
      paymentMethod: params.has('paymentMethod') ? (params.get('paymentMethod') as OrderPaymentMethod) : undefined,
      locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
      customerId: params.has('customerId') ? parseInt(params.get('customerId')!) : undefined,
      type: params.has('type') ? (params.get('type') as OrderType) : undefined,
      shippingType: params.has('shippingType') ? [params.get('shippingType') as ShippingType] : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setIsTableDataLoading(true);

      // Extract tab from request
      const tabFilters: Pick<OrderSearchRequest, 'status' | 'returnStatus'> = {
        status: undefined,
        returnStatus: undefined,
      };

      switch (_request.tab) {
        case 'Reservations':
          tabFilters.status = OrderStatus.Requested;
          tabFilters.returnStatus = undefined;
          break;
        case 'Approved':
          tabFilters.status = OrderStatus.Approved;
          tabFilters.returnStatus = OrderReturnStatus.NotStarted;
          break;
        case 'On Rent':
          tabFilters.status = OrderStatus.Approved;
          tabFilters.returnStatus = OrderReturnStatus.OnRent;
          break;
        case 'Off Rent':
          tabFilters.status = OrderStatus.Approved;
          tabFilters.returnStatus = OrderReturnStatus.OffRent;
          break;
        case 'Completed':
          tabFilters.status = OrderStatus.Approved;
          tabFilters.returnStatus = OrderReturnStatus.Complete;
          break;
        case 'Cancelled':
          tabFilters.status = OrderStatus.Cancelled;
          tabFilters.returnStatus = undefined;
          break;
        case 'Quote':
          tabFilters.status = OrderStatus.Quote;
          tabFilters.returnStatus = undefined;
          break;
        case 'Missed':
          tabFilters.status = OrderStatus.Missed;
          tabFilters.returnStatus = undefined;
          break;
      }

      searchOrders({
        ..._request,
        ...tabFilters,
        sortBy: 'id',
        sortDirection: 'desc',
      })
        .then(({ items, pagination }) => {
          setTableData(items.map((item) => buildDataRow(item, locations, unitTrackingField)));
          setTablePagination(pagination);
          setIsTableDataLoading(false);

          // Avoid to send more requests if pagination changes
          // const { pageNumber, pageSize, ...newRequest } = request;
          // const stringRequest = JSON.stringify(newRequest);

          // if (stringRequest !== lastOrderTotalRequest) {
          // setLastOrderTotalRequest(stringRequest);
          setIsResumeLoading(true);
          getOrdersResume({
            ...request,
            ...tabFilters,
          }).then((response) => {
            setOrderTotals(response);
            setIsResumeLoading(false);
          });
          // }
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
    [locations, unitTrackingField],
    ['tab'],
  );

  const PaymentMethodValues: SearchDropdownOption[] = [
    { label: 'Credit Card', value: OrderPaymentMethod.CreditCard },
    { label: 'On Account', value: OrderPaymentMethod.OnAccount },
    { label: 'Proceed Without Payment', value: OrderPaymentMethod.ProceedWithoutPayment },
    { label: 'Cash', value: OrderPaymentMethod.Cash },
    { label: 'Check', value: OrderPaymentMethod.Check },
  ];

  const locationOptions: SearchDropdownOption[] = locations.map(
    (loc) =>
      ({
        label: loc.name,
        value: loc.id + '',
      } as SearchDropdownOption),
  );

  const orderTypeOptions: SearchDropdownOption[] = [
    {
      value: OrderType.Sale,
      label: 'Sell',
    },
    {
      value: OrderType.Rental,
      label: 'Rent',
    },
  ];

  const deliveryTypeOptions = uniqBy(
    Object.values(ShippingType).map((value) => ({ value, label: formatShippingType(value) })),
    (option) => option.label,
  );

  const columns: DataTableColumn[] = [
    { key: 'OrderNumber', label: 'Order No' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'Location', label: 'Location' },
    { key: 'RequestedTime', label: 'Requested Time' },
    { key: 'RentalStart', label: 'Rental Start' },
    { key: 'RentalEnd', label: 'Rental End' },
    { key: 'Status', label: 'Request Status' },
    { key: 'PaymentMethod', label: 'Payment Method' },
    { key: 'AssignedUnit', label: 'Product(s)' },
    { key: 'Quantity', label: 'QTY' },
    { key: 'SubTotal', label: 'Sub Total' },
    { key: 'ProductFees', label: 'Product Fees' },
    { key: 'ProductFeesTotal', label: 'Total Product Fees' },
    { key: 'OrderFees', label: 'Additional Fees' },
    { key: 'OrderFeesTotal', label: 'Total Additional Fees' },
    { key: 'Tax', label: 'Tax' },
    { key: 'Delivery', label: 'Delivery' },
    { key: 'PickUp', label: 'PickUp' },
    { key: 'DamageWaiver', label: 'Damage Waiver' },
    { key: 'CartTotal', label: 'Cart Total' },
    { key: 'AdditionalDiscount', label: 'Additional Discount' },
    { key: 'CouponCode', label: 'Coupon Code' },
    { key: 'CouponCodeDiscount', label: 'Coupon Code Discount' },
    { key: 'TotalCharged', label: 'Total Charged' },
    { key: 'UpchargeReasons', label: 'Upcharge Reasons' },
    { key: 'TotalUpcharge', label: 'Total Upcharge' },
    { key: 'RefundReasons', label: 'Refund Reasons' },
    { key: 'TotalRefund', label: 'Total Refund' },
    { key: 'GrandTotal', label: 'Grand Total' },
    { key: 'BillingAddress', label: 'Billing Address' },
    { key: 'DeliveryAddress', label: 'Delivery Address' },
    { key: 'OrderNotes', label: 'Notes' },
    { key: 'InternalNotes', label: 'Internal Notes' },
    { key: 'OrderPONumber', label: 'Order PO Number' },
  ];

  const buildDataRow = (order: Order, locations: Location[], trackingVia: 'serialNumber' | 'unitNumber') => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${order.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {order.orderNumber || 'N/A'}
            </QuipliLink>
          </Typography>
        </>
      ),
      fields: {
        OrderNumber: <OrderNumberCell order={order} />,
        CustomerName: <CustomerNameCell order={order} />,
        RequestedTime: <DateTimeCell date={order.dateAdded} />,
        Location: <LocationCell order={order} locations={locations} />,
        RentalStart: <RentalStartCell order={order} />,
        RentalEnd: <RentalEndCell order={order} />,
        Status: <OrderStatusCell order={order} />,
        PaymentMethod: <PaymentMethodCell order={order} />,
        AssignedUnit: <AssignedUnitCell order={order} trackingVia={trackingVia} />,
        Quantity: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {order.products.reduce((total, product) => +total + +product.quantity, 0)}
          </Typography>
        ),
        SubTotal: <FormatAmountCell amount={order.cartTotal} order={order} />,
        ProductFees: <ProductFeesCell order={order} />,
        ProductFeesTotal: <ProductFeesTotalCell order={order} />,
        OrderFees: <OrderFeesCell order={order} />,
        OrderFeesTotal: <OrderFeesTotalCell order={order} />,
        Tax: <TotalTaxAmountCell order={order} />,
        Delivery: <FormatAmountCell amount={sumByLineItemType(order.invoices, 'Delivery')} order={order} />,
        PickUp: <FormatAmountCell amount={sumByLineItemType(order.invoices, 'Pickup')} order={order} />,
        DamageWaiver: <FormatAmountCell amount={sumByLineItemType(order.invoices, 'DamageWaiver')} order={order} />,
        CartTotal: (
          <FormatAmountCell
            amount={order.discountTotal ? order.netCharged - order.discountTotal : order.netCharged}
            order={order}
          />
        ),
        AdditionalDiscount: (
          <FormatAmountCell amount={sumByLineItemType(order.invoices, 'AdditionalDiscount')} order={order} />
        ),
        CouponCode: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {order.discountCoupon ?? 'N/A'}
          </Typography>
        ),
        CouponCodeDiscount: <FormatAmountCell amount={sumByLineItemType(order.invoices, 'Coupon')} order={order} />,
        TotalCharged: <FormatAmountCell amount={order.netCharged} order={order} />,
        UpchargeReasons: <UpchargeReasonsCell order={order} />,
        TotalUpcharge: <TotalUpchargeAmountCell order={order} />,
        RefundReasons: <RefundReasonsCell order={order} />,
        TotalRefund: <TotalRefundAmountCell order={order} />,
        GrandTotal: <TotalAmountCell order={order} />,
        BillingAddress: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            <AddressCell address={order.billingAddress} />
          </Typography>
        ),
        DeliveryAddress: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            <AddressCell address={order.shippingAddress} />
          </Typography>
        ),
        OrderNotes: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {order.notes !== '' && order.notes != null ? order.notes : 'N/A'}
          </Typography>
        ),
        InternalNotes: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {order.internalNotes.length > 0 ? order.internalNotes.flatMap((note) => note.comment).join(',') : 'N/A'}
          </Typography>
        ),
        OrderPONumber: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            {order.poNumber !== '' && order.poNumber != null ? order.poNumber : 'N/A'}
          </Typography>
        ),
      },
    };
  };

  const clearAll = () => {
    setRequestedAfter(undefined);
    setRequestedBefore(undefined);
    setRentalStartStartDate(undefined);
    setRentalStartEndDate(undefined);
    setRentalEndStartDate(undefined);
    setRentalEndEndDate(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedUser(undefined);
    setSelectedOrderType(undefined);
    setSelectedLocation(undefined);
    setSelectedDeliveryType(undefined);
    setSelectedPaymentMethod(undefined);

    if (searchRef.current?.setKeyword) {
      searchRef.current.setKeyword('');
    }

    setRequest((prevState) => ({
      tab: 'All',
      pageNumber: 1,
      pageSize: prevState.pageSize,
      keyword: '',
      status: undefined,
      returnStatus: undefined,
      from: undefined,
      to: undefined,
      requestedAfter: undefined,
      requestedBefore: undefined,
      startDateFrom: undefined,
      startDateTo: undefined,
      endDateFrom: undefined,
      endDateTo: undefined,
      customerId: undefined,
      type: undefined,
      shippingType: undefined,
      locationId: undefined,
    }));
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      const [confs, locs] = await Promise.all([
        getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_TIMEZONE']),
        listLocations(),
      ]);

      setUnitTrackingField(
        confs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
      );
      setTimezone(confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value);
      setLocations(locs);
    };
    fetchConfigs();

    setRentalStartStartDate(params.has('startDateFrom') ? DateTime.fromISO(params.get('startDateFrom')!) : undefined);
    setRentalStartEndDate(params.has('startDateTo') ? DateTime.fromISO(params.get('startDateTo')!) : undefined);
    setRentalEndStartDate(params.has('endDateFrom') ? DateTime.fromISO(params.get('endDateFrom')!) : undefined);
    setRentalEndEndDate(params.has('endDateTo') ? DateTime.fromISO(params.get('endDateTo')!) : undefined);
    setStartDate(params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined);
    setEndDate(params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined);
    setSelectedPaymentMethod(
      params.has('paymentMethod') ? (params.get('paymentMethod') as OrderPaymentMethod) : undefined,
    );
    setSelectedLocation(params.has('locationId') ? parseInt(params.get('locationId')!) : undefined);
    setSelectedOrderType(params.has('type') ? (params.get('type') as OrderType) : undefined);
    setSelectedDeliveryType(params.has('shippingType') ? (params.get('shippingType') as ShippingType) : undefined);

    if (params.has('customerId')) {
      getUserById(parseInt(params.get('customerId')!)).then(setSelectedUser);
    }
  }, []);

  // Callbacks
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const file = await exportTransactionsReport(request);

      const exportFileName = `Transactions_Report_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e: any) {
      toast('Something went wrong while exporting the report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const filtersTabNames = [
    { name: 'All' },
    { name: 'Reservations' },
    { name: 'Approved' },
    { name: 'On Rent' },
    { name: 'Off Rent' },
    { name: 'Completed' },
    { name: 'Cancelled' },
    { name: 'Quote' },
    { name: 'Missed' },
  ];

  const getTabContent = (tab: string): null => {
    switch (tab) {
      case 'All':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'All',
        }));
        return null;
      case 'Reservations':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Reservations',
        }));
        return null;
      case 'Approved':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Approved',
        }));
        return null;
      case 'On Rent':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'On Rent',
        }));
        return null;
      case 'Off Rent':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Off Rent',
        }));
        return null;
      case 'Completed':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Completed',
        }));
        return null;
      case 'Cancelled':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Cancelled',
        }));
        return null;
      case 'Quote':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Quote',
        }));
        return null;
      case 'Missed':
        setRequest((prevState) => ({
          ...prevState,
          tab: 'Missed',
        }));
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="order-list container-v2">
      <div className="flex justify-between items-center">
        <Typography type="display-sm" weight="medium">
          Transactions Report
        </Typography>
      </div>

      <div className="pt-10 pb-3 flex items-center justify-between">
        <div className="flex gap-2">
          <SearchBar
            ref={searchRef}
            data-cy="search-transactions"
            placeholder="Search by keyword"
            defaultValue={request.keyword}
            setRequest={setRequest}
          />
          <Button
            className="button-secondary !border-0 h-[46px] relative"
            onClick={() => setFiltersModalOpen(true)}
            data-cy="filter-order"
          >
            <Typography
              type="text-md"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
            </Typography>

            {activeFilters > 0 && (
              <Badge className="absolute -right-2 -top-2" text={`${activeFilters}`} size="small" type="yellow" />
            )}
          </Button>

          {activeFilters > 0 && (
            <Button
              className="button-secondary !border-0 h-[46px]"
              onClick={() => clearAll()}
              data-cy="clear-filter-order"
            >
              <Typography
                type="text-md"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Clear Filters
              </Typography>
            </Button>
          )}
        </div>
        <div className="hidden md:block">
          <div className="flex flex-end">
            <Button className="button-primary md" data-cy="export" onClick={exportToCSV}>
              <Typography type="text-md" weight="regular" className="text-[var(--quipli-white-50)]">
                Export
              </Typography>
              <Icon icon="Export" size="medium" stroke="var(--quipli-white-50)" />
            </Button>
          </div>
        </div>
      </div>
      <div className="block md:hidden">
        <div className="flex pb-5">
          <Button className="button-primary md" data-cy="export_transactions" onClick={exportToCSV}>
            <Typography type="text-md" weight="regular" className="text-[var(--quipli-white-50)]">
              Export
            </Typography>
            <Icon icon="Export" size="medium" stroke="var(--quipli-white-50)" />
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-wrap mb-3">
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Grand Total
          </Typography>

          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.grandTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Orders
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? orderTotals.noOrders : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Customer Product Fees
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.productFeesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Additional Fees
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.additionalFeesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Tax
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.taxesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Delivery Fees
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.deliveryFeesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Damage Waiver Fees
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.damageWaiverFeesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Coupons or Discounts
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.discountTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Upcharges
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.upchargesTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
        <div className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)]">
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
            Total Refunded
          </Typography>
          {isTableDataLoading || isResumeLoading ? (
            <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
          ) : (
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {orderTotals ? displayMoneyFormat(orderTotals.refundsTotal, locations[0]?.currency) : null}
            </Typography>
          )}
        </div>
      </div>

      <FiltersModal
        isOpen={filtersModalOpen}
        title="Filters"
        onConfirm={() => {
          setFiltersModalOpen(false);
          setIsTableDataLoading(true);
          setRequest((prevState) => ({
            ...prevState,
            ...{
              from: startDate,
              to: endDate,
              requestedAfter: requestedAfter,
              requestedBefore: requestedBefore,
              startDateFrom: rentalStartStartDate,
              startDateTo: rentalStartEndDate,
              endDateFrom: rentalEndStartDate,
              endDateTo: rentalEndEndDate,
              customerId: selectedUser?.id,
              type: selectedOrderType,
              shippingType: selectedDeliveryType !== undefined ? [selectedDeliveryType] : undefined,
              locationId: selectedLocation,
              paymentMethod: selectedPaymentMethod,
            },
          }));
        }}
        onCancel={() => {
          setFiltersModalOpen(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Rental Start Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={rentalStartStartDate}
                onChange={(d) => setRentalStartStartDate(d?.setZone(timezone, { keepLocalTime: true }).startOf('day'))}
              />
              <DatePicker
                value={rentalStartEndDate}
                onChange={(d) => setRentalStartEndDate(d?.setZone(timezone, { keepLocalTime: true }).endOf('day'))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Rental End Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={rentalEndStartDate}
                onChange={(d) => setRentalEndStartDate(d?.setZone(timezone, { keepLocalTime: true }).startOf('day'))}
              />
              <DatePicker
                value={rentalEndEndDate}
                onChange={(d) => setRentalEndEndDate(d?.setZone(timezone, { keepLocalTime: true }).endOf('day'))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Rental Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={startDate}
                onChange={(d) => setStartDate(d?.setZone(timezone, { keepLocalTime: true }).startOf('day'))}
              />
              <DatePicker
                value={endDate}
                onChange={(d) => setEndDate(d?.setZone(timezone, { keepLocalTime: true }).endOf('day'))}
                disabledIntervals={[Interval.before(startDate || DateTime.now(), { years: 10 })]}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Requested Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={requestedAfter}
                onChange={(requestedAfter) =>
                  setRequestedAfter(requestedAfter?.setZone(timezone, { keepLocalTime: true }).startOf('day'))
                }
              />
              <DatePicker
                value={requestedBefore}
                onChange={(requestedBefore) =>
                  setRequestedBefore(requestedBefore?.setZone(timezone, { keepLocalTime: true }).endOf('day'))
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Payment Method
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                defaultValue={PaymentMethodValues.find((elem) => elem.value === selectedPaymentMethod)}
                options={PaymentMethodValues}
                onChange={(ps) => {
                  setSelectedPaymentMethod(ps?.value as any);
                }}
                isClearable={true}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Location
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                defaultValue={
                  selectedLocation
                    ? ({
                        value: `${selectedLocation}` || undefined,
                        label: locationOptions.find((loc) => loc.value === `${selectedLocation}`)?.label,
                      } as SearchDropdownOption)
                    : undefined
                }
                options={locationOptions}
                onChange={(ps) => setSelectedLocation(ps?.value as any)}
                isClearable={true}
                menuPlacement="bottom"
                placeholder="Select"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Customer
            </Typography>
            <div className="flex gap-2 w-full">
              <UserSearch locations={locations} selectedUser={selectedUser} onSelectUser={setSelectedUser} />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Order Type
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                defaultValue={orderTypeOptions.filter((elem) => elem.value === selectedOrderType)}
                options={orderTypeOptions}
                onChange={(ps) => setSelectedOrderType(ps?.value as any)}
                isClearable={true}
                menuPlacement="bottom"
                placeholder="Select"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Delivery Type
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                defaultValue={deliveryTypeOptions.filter((elem) => elem.value === selectedDeliveryType)}
                options={deliveryTypeOptions}
                onChange={(ps) => setSelectedDeliveryType(ps?.value as any)}
                isClearable={true}
                menuPlacement="auto"
                placeholder="Select"
              />
            </div>
          </div>
        </div>
      </FiltersModal>

      <div className="flex w-full items-center justify-start gap-2 mb-3 overflow-x-auto">
        <TabNavRequest
          tabNames={filtersTabNames}
          selectedTab={request.tab!}
          onSelect={getTabContent}
          className="bg-transparent !mt-0"
        />
      </div>

      <DataTable
        isLoading={isTableDataLoading}
        columnNames={columns}
        data={tableData}
        pagination={tablePagination}
        request={request}
        setRequest={setRequest}
        cypressPrevious="previous_transactions_page"
        cypressNext="next_transactions_page"
      />

      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Transactions'} />
    </div>
  );
};
